import React, { useState } from 'react';
import { X, Play } from 'lucide-react';
import { ProviderSelector } from './ProviderSelector';
import { AgentType, AgentModelOverride } from '../types/models';

interface SelectedCustomConfig {
  id: string;
  name: string;
  config: Record<AgentType, AgentModelOverride | undefined>;
}

export interface HallucinationRunSelection {
  /** When a predefined provider is selected */
  providerId?: string;
  /** When a custom config is selected */
  customConfig?: SelectedCustomConfig;
}

interface HallucinationRunModalProps {
  activeTestCount: number;
  onClose: () => void;
  onStart: (selection: HallucinationRunSelection) => void;
}

export const HallucinationRunModal: React.FC<HallucinationRunModalProps> = ({
  activeTestCount,
  onClose,
  onStart,
}) => {
  const [selectedId, setSelectedId] = useState('');
  const [selectedCustomConfig, setSelectedCustomConfig] = useState<SelectedCustomConfig | null>(null);
  // Track whether the selected ID belongs to a custom config
  const [isCustomSelection, setIsCustomSelection] = useState(false);

  const handleStart = () => {
    if (!selectedId) return;
    if (isCustomSelection && selectedCustomConfig) {
      onStart({ customConfig: selectedCustomConfig });
    } else {
      onStart({ providerId: selectedId });
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />
      <div data-component="HallucinationRunModal" className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Select Configuration</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <p className="text-sm text-gray-600 mb-4">
              Select a configuration to run the {activeTestCount} active tests.
            </p>

            <ProviderSelector
              selectedProviders={selectedId ? [selectedId] : []}
              onSelectionChange={(ids) => {
                setSelectedId(ids[0] || '');
                // If the new selection doesn't match the custom config, it's a predefined provider
                if (ids[0] && ids[0] !== selectedCustomConfig?.id) {
                  setIsCustomSelection(false);
                }
              }}
              mode="evaluation-single"
              hideAdvancedToggle={true}
              hideActions={true}
              onCustomConfigsChange={(configs) => {
                if (configs.length > 0) {
                  setSelectedId(configs[0].id);
                  setSelectedCustomConfig(configs[0]);
                  setIsCustomSelection(true);
                }
              }}
            />
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleStart}
              disabled={!selectedId}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Play className="w-4 h-4" />
              Start Run
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
